import {DosageLimitsFormMatrix} from '@/modules/drug-catalog/components/drug-item-form/dosage-limits-form-matrix'
import {useDoseUnitField} from '@/modules/drug-catalog/hooks/use-dose-unit-field'
import {
  doseCalculationMethodOptions,
  durationUnitOptions,
  overdosePreventionLevelOptions,
  roundingModeOptions,
} from '@/modules/drug-catalog/substances/constants'
import {getSubstanceFormDefaultValues} from '@/modules/drug-catalog/substances/functions'
import {useGetUnits} from '@/modules/products/units/api'
import {mapUnitsToSelectItems} from '@/modules/products/units/functions'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {FormGrid} from '@cypro/ui/components/ui/form-grid'
import {withForm} from '@cypro/ui/integrations/form'
import {Grid} from '@chakra-ui/react'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'

export const DosingSafetyTab = withForm({
  defaultValues: getSubstanceFormDefaultValues(undefined),
  props: {drugId: undefined as string | undefined},
  render: function Render({form, drugId}) {
    const {t} = useTranslate('common')
    const {t: tSubstances} = useTranslate('substances')

    const [unitId] = useStore(form.store, state => state.values.doseUnitId)
    const {units} = useGetUnits({filters: {type: 'mass'}})
    const unit = units?.find(u => u.id === unitId)

    const unitsSelectItems = mapUnitsToSelectItems(units)
    const doseUnit = useDoseUnitField({
      drugId,
      unitId,
      units,
      getDoseLimits: () => form.getFieldValue('doseLimits'),
      setDoseLimits: limits => form.setFieldValue('doseLimits', limits),
      restoreUnit: id => form.setFieldValue('doseUnitId', [id], {dontRunListeners: true}),
    })
    const roundingModeSelectItems = roundingModeOptions.map(o => ({value: o, label: t(o)}))
    const doseCalcMethodSelectItems = doseCalculationMethodOptions.map(v => ({value: v, label: t(v)}))
    const overdoseSelectItems = overdosePreventionLevelOptions.map(v => ({label: t(v), value: v}))

    return (
      <Grid gapY="8">
        <FormSection
          label={tSubstances('intervals_frequencies')}
          description={tSubstances('intervals_frequencies_description')}
        >
          <FormGrid gridTemplateColumns="16rem 16rem">
            <form.AppField name="minIntervalValue">
              {field => (
                <field.NumberInput
                  size="sm"
                  label={t('min_interval_between_dose')}
                  placeholder={tSubstances('min_interval_placeholder')}
                  min={0}
                />
              )}
            </form.AppField>

            <form.AppField name="minIntervalUnit">
              {field => (
                <field.Select
                  size="sm"
                  label={t('min_interval_unit')}
                  placeholder={t(durationUnitOptions[0])}
                  items={durationUnitOptions.map(v => ({label: t(v), value: v}))}
                />
              )}
            </form.AppField>
          </FormGrid>
        </FormSection>

        <FormSection
          label={tSubstances('dosing_rules_safety_baseline')}
          description={tSubstances('dosing_rules_safety_baseline_description')}
        >
          <FormGrid>
            <form.AppField name="doseCalculationMethod">
              {field => (
                <field.Select
                  size="sm"
                  label={t('dose_calculation_mode')}
                  items={doseCalcMethodSelectItems}
                  placeholder={t('eg', {value: doseCalcMethodSelectItems?.[0]?.label})}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="doseUnitId" listeners={{onChange: ({value}) => doseUnit.onUnitChange(value[0])}}>
              {field => (
                <field.Select
                  size="sm"
                  label={t('dose_unit')}
                  placeholder={t('eg', {value: unitsSelectItems?.[0]?.label})}
                  items={unitsSelectItems}
                  disabled={doseUnit.disabled}
                  helpText={doseUnit.helpText}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="precision">
              {field => (
                <field.NumberInput
                  size="sm"
                  label={t('precision_decimals')}
                  placeholder={tSubstances('precision_placeholder')}
                  min={0}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="roundingMode">
              {field => (
                <field.Select
                  size="sm"
                  label={t('rounding_mode')}
                  items={roundingModeSelectItems}
                  placeholder={t('eg', {value: roundingModeSelectItems?.[0]?.label})}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="overdosePreventionLevel">
              {field => (
                <field.Select
                  size="sm"
                  label={t('overdose_prevention_level')}
                  placeholder={t('eg', {value: overdoseSelectItems?.[0]?.label})}
                  items={overdoseSelectItems}
                  required
                />
              )}
            </form.AppField>
          </FormGrid>
        </FormSection>

        <FormSection
          label={tSubstances('maximum_dosages_matrix')}
          description={tSubstances('maximum_dosages_matrix_description')}
        >
          <DosageLimitsFormMatrix form={form} fields="doseLimits" unit={unit?.ucumCode} />
        </FormSection>
      </Grid>
    )
  },
})
